import { useMemo, useState } from 'react';
import { createFileRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import { EntityIndexLayout, WorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import { GroupListPanel } from '@/components/organisms/settings-groups/GroupListPanel';
import { GroupTableView } from '@/components/organisms/settings-groups/GroupTableView';
import { useDeleteGroup } from '@/hooks/settings/useGroups';
import type { GroupListItem } from '@/types/settings';

export const Route = createFileRoute('/_dashboard/dashboard/settings/groups')({
  component: GroupsPage,
});

// Manual pathname derivation mirrors the roles mold (regex/string matching
// rather than useEntityRouteState).
const GROUP_DETAIL_ROUTE_PATTERN = /^\/dashboard\/settings\/groups\/([^/]+)\/?$/;
const GROUP_EDIT_ROUTE_PATTERN = /^\/dashboard\/settings\/groups\/([^/]+)\/edit\/?$/;
const GROUP_NEW_ROUTE_PATTERN = /^\/dashboard\/settings\/groups\/new\/?$/;
const GROUP_INDEX_ROUTE_PATTERN = /^\/dashboard\/settings\/groups\/?$/;

function GroupsPage() {
  const router = useRouter();
  const { location } = useRouterState();
  const deleteGroup = useDeleteGroup();

  const [deleteTarget, setDeleteTarget] = useState<GroupListItem | null>(null);

  const detailRouteGroupId = location.pathname.match(GROUP_DETAIL_ROUTE_PATTERN)?.[1];
  const detailGroupId =
    detailRouteGroupId && detailRouteGroupId !== 'new' ? detailRouteGroupId : null;
  const editGroupId = location.pathname.match(GROUP_EDIT_ROUTE_PATTERN)?.[1];
  const selectedId = editGroupId ?? detailGroupId ?? undefined;
  const isNewRoute = GROUP_NEW_ROUTE_PATTERN.test(location.pathname);
  const isEditRoute = Boolean(editGroupId);
  const isFormMode = isNewRoute || isEditRoute;
  const hasChildRoute = Boolean(selectedId) || isNewRoute;
  const isIndexRoute = GROUP_INDEX_ROUTE_PATTERN.test(location.pathname);

  function openDetail(groupId: string) {
    void router.navigate({ to: '/dashboard/settings/groups/$id', params: { id: groupId } });
  }

  // Auto-select (split desktop) replaces history so Back doesn't loop.
  function autoSelectDetail(groupId: string) {
    void router.navigate({
      to: '/dashboard/settings/groups/$id',
      params: { id: groupId },
      replace: true,
    });
  }

  function openEdit(groupId: string) {
    void router.navigate({
      to: '/dashboard/settings/groups/$id/edit',
      params: { id: groupId },
    });
  }

  return (
    <WorkspaceShell hideHeader={isFormMode}>
      <GroupsHeaderSlot
        hidden={isFormMode}
        showTitleFallback={!isFormMode && !selectedId}
        onNewGroup={() => void router.navigate({ to: '/dashboard/settings/groups/new' })}
      />

      <EntityIndexLayout
        title="Groups"
        storageKey="groups-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() => void router.navigate({ to: '/dashboard/settings/groups' })}
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        splitWidthClass="w-[360px]"
        tableSheetWidthClass="w-full sm:max-w-lg"
        hideListInSplit={isFormMode}
        fullPageFormInTable
        splitList={() => (
          <div className="flex h-full flex-col">
            <GroupListPanel
              selectedId={selectedId}
              onSelect={openDetail}
              onAutoSelect={autoSelectDetail}
              isIndexRoute={isIndexRoute}
            />
          </div>
        )}
        splitDetail={<Outlet />}
        tableView={({ openInSheet }) => (
          <GroupTableView
            onView={(groupId) => openInSheet(() => openDetail(groupId))}
            onEdit={(groupId) => openEdit(groupId)}
            onDelete={(group) => setDeleteTarget(group)}
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="flex h-full flex-col md:hidden">
            {isIndexRoute ? (
              <GroupListPanel
                selectedId={selectedId}
                onSelect={openDetail}
                onAutoSelect={autoSelectDetail}
                isIndexRoute={isIndexRoute}
              />
            ) : (
              <Outlet />
            )}
          </div>
        }
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete group?</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {deleteTarget ? (
                <>
                  Delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
                  {deleteTarget.memberCount > 0 ? (
                    <> This removes {deleteTarget.memberCount} member(s) from the group.</>
                  ) : null}
                </>
              ) : null}
            </p>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={deleteGroup.isPending}
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteGroup.isPending}
              onClick={() => {
                if (!deleteTarget) return;
                deleteGroup.mutate(deleteTarget.id, {
                  onSuccess: () => setDeleteTarget(null),
                });
              }}
            >
              {deleteGroup.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </WorkspaceShell>
  );
}

function GroupsHeaderSlot({
  hidden,
  showTitleFallback,
  onNewGroup,
}: {
  hidden: boolean;
  showTitleFallback: boolean;
  onNewGroup: () => void;
}) {
  const newButton = useMemo(() => {
    if (hidden) return null;
    return (
      <Button size="sm" title="Add group" onClick={onNewGroup}>
        <Plus size={14} />
        Add group
      </Button>
    );
  }, [hidden, onNewGroup]);

  // Fallback franja title when no group is selected (table view, or the brief
  // index moment). When a group is open the detail route's own slot supplies the
  // richer context, taking over this key.
  const contextHeader = useMemo(() => {
    if (!showTitleFallback) return null;
    return <h1 className="truncate text-base font-semibold">Groups</h1>;
  }, [showTitleFallback]);

  useWorkspaceHeader({ newButton, contextHeader }, [newButton, contextHeader]);
  return null;
}
